import React from "react"
import { Box, Typography } from "@mui/material"
import { ButtonLoadingSeeker, IconSeeker } from "@ui-seeker/core"
import { AppBulkEditAction, AppDialogCancel, secondaryButtonSx } from "@components"
import { dsColorNew } from "@palette"
import { dsRepricingDrawerData, dsRepricingDrawerErrors, clearDsRepricingSignals } from "Pages/DsRepricing/Page/@signals"
import {
	DS_REPRICING_STEPS, DS_REPRICING_TOTAL_STEPS, FIRST_SAVEABLE_STEP,
	formIsValid, stepIsValid,
} from "Pages/DsRepricing/Page/Drawer/steps"

// The one action the wizard wants you to take, sized like it. Everything
// else in the bar is an escape hatch and reads as one: a wizard where
// Cancel, Back and Next are three identical outlined buttons is a wizard
// that has not told you where the way forward is.
const primarySx = { minWidth: 170 }

export default function DsRepricingDrawerFooter({ onSubmit, isLoading, current, onStepChange, noFit = false }) {
	const errors = dsRepricingDrawerErrors.value
	const isLastStep = current === DS_REPRICING_TOTAL_STEPS

	// Next only judges the step in front of the user. Saving judges the whole form, because an
	// optional guard left half-configured is still invalid even if the user never scrolled to it.
	const canAdvance = stepIsValid(current, errors)
	const canSave = formIsValid(errors)
	const canSaveEarly = current >= FIRST_SAVEABLE_STEP && !isLastStep

	const submit = () => onSubmit(dsRepricingDrawerData.value)

	// Naming the destination is what turns "Next" into a step: the
	// operator knows what they are about to be asked before they commit
	// to being asked it.
	const nextLabel = DS_REPRICING_STEPS.find(s => s.step === current + 1)?.label

	// No rate card fits the scope (CU-869fh646w, UX D5): said in red on
	// every step that can save, with the way back to fix it. Save stays
	// enabled — the strategy is valid, it will just decide nothing.
	const showNoFit = noFit && current >= FIRST_SAVEABLE_STEP

	return (
		<Box sx={{ width: '100%' }}>
		{showNoFit && (
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, pb: 1 }}>
				<IconSeeker icon="circle-exclamation" sx={{ fontSize: 13, color: dsColorNew.dangerRed }}/>
				<Typography sx={{ color: dsColorNew.dangerRed, fontSize: 13, fontWeight: 500 }}>
					No rate card fits this scope — this strategy will decide nothing.
				</Typography>
				<ButtonLoadingSeeker variant="text" size="small" onClick={() => onStepChange(2)}
				                     sx={{ textTransform: 'none', fontSize: 13, fontWeight: 600, color: dsColorNew.dangerRed, py: 0, borderRadius: '8px' }}>
					Back to Scope
				</ButtonLoadingSeeker>
			</Box>
		)}
		<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, px: 1, width: '100%' }}>
			<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted }}>
				Step {current} of {DS_REPRICING_TOTAL_STEPS}
				{current < FIRST_SAVEABLE_STEP && (
					<Box component="span" sx={{ color: dsColorNew.textSubtle }}> · you can save from step {FIRST_SAVEABLE_STEP}</Box>
				)}
			</Typography>

			<Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
				{/* Cancel is the quietest thing in the bar. It throws the
				    work away and it used to look exactly like Next. */}
				<AppDialogCancel onClick={clearDsRepricingSignals}/>

				{current > 1 && (
					<AppDialogCancel startIcon={<IconSeeker icon="arrow-left"/>}
					                 onClick={() => onStepChange(current - 1)}>
						Back
					</AppDialogCancel>
				)}

				{canSaveEarly && (
					<ButtonLoadingSeeker
						variant="outlined"
						loading={isLoading}
						startIcon={<IconSeeker icon="floppy-disk" tooltip="Save and exit"/>}
						disabled={!canSave}
						onClick={submit}
						sx={{ ...secondaryButtonSx, height: 36 }}
					>
						Save &amp; exit
					</ButtonLoadingSeeker>
				)}

				{isLastStep ? (
					<AppBulkEditAction
						loading={isLoading}
						startIcon={<IconSeeker icon="floppy-disk" tooltip="Save"/>}
						disabled={!canSave}
						onClick={submit}
						sx={primarySx}
					>
						Save configuration
					</AppBulkEditAction>
				) : (
					<AppBulkEditAction disabled={!canAdvance}
					                   endIcon={<IconSeeker icon="arrow-right"/>}
					                   onClick={() => onStepChange(current + 1)}
					                   sx={primarySx}>
						{nextLabel ? `Next: ${nextLabel}` : 'Next'}
					</AppBulkEditAction>
				)}
			</Box>
		</Box>
		</Box>
	)
}